import { GenericError } from '../../errors/shared/genericError.js';
import { ValidationError } from '../../errors/shared/base.js';
import { kStructuredCloneMarker, kSendMarker, isStructuredClone, Clone } from '../../threads/shared/transfer/transfer.js';
import { restore } from '../../threads/shared/transfer/restoreRegistry.js';
import '../../iterator/shared/iterator.js';
import { Some, None } from '../../option/shared/option.js';

/* oxlint-disable max-lines */
const NAMESPACE = 'core.result';
const OkType = 'Ok';
const ErrType = 'Err';
class OkImpl {
    '@type' = OkType;
    Ok;
    [kStructuredCloneMarker] = NAMESPACE;
    [kSendMarker] = 1;
    constructor(value) {
        this.Ok = value;
    }
    get _val() {
        return this.Ok;
    }
    isOk() {
        return true;
    }
    isOkAnd(f) {
        return f(this.Ok);
    }
    isErr() {
        return false;
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    isErrAnd(f) {
        return false;
    }
    ok() {
        return Some(this.Ok);
    }
    err() {
        return None();
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    expect(msg, domain) {
        return this.Ok;
    }
    unwrap() {
        return this.Ok;
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    unwrapOr(defaultValue) {
        return this.Ok;
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    unwrapOrElse(op) {
        return this.Ok;
    }
    andThen(op) {
        const result = op(this.Ok);
        if (!isResult(result)) {
            throw new GenericError({
                message: 'Из фукнции обратного вызова должно возвращаться значение типа Result',
                type: 'ResultValidationPanic',
                domain: 'peperrors.result.andThen',
            });
        }
        return result;
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    orElse(op) {
        return this;
    }
    map(op) {
        return Ok(op(this.Ok));
    }
    mapOr(defaultValue, op) {
        return op(this.Ok);
    }
    mapOrElse(defaultValue, f) {
        return f(this.Ok);
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    mapErr(op) {
        return this;
    }
    *[Symbol.iterator]() {
        yield this;
    }
    toStructuredClone() {
        if (isStructuredClone(this.Ok)) {
            const [marker, val, transferable] = this.Ok.toStructuredClone();
            let okStruct;
            if (transferable) {
                okStruct = [this[kStructuredCloneMarker], { Ok: [marker, val] }, transferable];
            }
            else {
                okStruct = [this[kStructuredCloneMarker], { Ok: [marker, val] }];
            }
            return okStruct;
        }
        const okStruct = [this[kStructuredCloneMarker], { Ok: this.Ok }];
        return okStruct;
    }
}
class ErrImpl {
    '@type' = ErrType;
    Err;
    [kStructuredCloneMarker] = NAMESPACE;
    [kSendMarker] = 1;
    constructor(err) {
        this.Err = err;
    }
    get _val() {
        return this.Err;
    }
    isOk() {
        return false;
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    isOkAnd(f) {
        return false;
    }
    isErr() {
        return true;
    }
    isErrAnd(f) {
        return f(this.Err);
    }
    ok() {
        return None();
    }
    err() {
        return Some(this.Err);
    }
    expect(message, domain) {
        let details;
        if (GenericError.is(this.Err) || this.Err instanceof Error) {
            details = this.Err;
        }
        throw new GenericError({
            type: 'ResultExpectPanic',
            message,
            domain: domain || NAMESPACE + '.err.expect',
            details,
        });
    }
    unwrap(domain) {
        if (GenericError.is(this.Err)) {
            throw this.Err;
        }
        throw new GenericError({
            type: 'ResultUnwrapError',
            message: 'Unwrap error result',
            domain: domain || NAMESPACE + '.err.unwrap',
            details: this.Err,
        });
    }
    unwrapOr(defaultValue) {
        return defaultValue;
    }
    unwrapOrElse(op) {
        return op(this.Err);
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    andThen(op) {
        return this;
    }
    orElse(op) {
        const result = op(this.Err);
        if (!isResult(result)) {
            throw new ValidationError({
                message: 'Из фукнции обратного вызова должно возвращаться значение типа Result',
                domain: NAMESPACE + '.orElse',
            });
        }
        return result;
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    map(op) {
        return this;
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    mapOr(defaultValue, op) {
        return defaultValue;
    }
    // oxlint-disable-next-line typescript/no-unused-vars
    mapOrElse(defaultValue, f) {
        return defaultValue(this.Err);
    }
    mapErr(op) {
        return Err(op(this.Err));
    }
    *[Symbol.iterator]() {
        yield this;
    }
    toStructuredClone() {
        if (isStructuredClone(this.Err)) {
            const [marker, val, transferable] = this.Err.toStructuredClone();
            let errStruct;
            if (transferable) {
                errStruct = [this[kStructuredCloneMarker], { Err: [marker, val] }, transferable];
            }
            else {
                errStruct = [this[kStructuredCloneMarker], { Err: [marker, val] }];
            }
            return errStruct;
        }
        const errStruct = [this[kStructuredCloneMarker], { Err: this.Err }];
        return errStruct;
    }
}
/**
 * Создает {@linkcode Result} типа `Ok` (успешно). Имплементирует
 * интерфейс {@linkcode IOkImpl}
 *
 * @export
 * @template T
 * @param {T} val
 * @return {IOkImpl<T>}
 */
function Ok(val) {
    return new OkImpl(val);
}
const OK_EMPTY = Ok(undefined);
/**
 * Возвращает ссылку на пустой результат, удобно использовать,
 * если `Result<undefined, GenericError>`;
 */
Ok.Empty = OK_EMPTY;
Ok.is = isOk;
Ok.isStruct = isOkStruct;
/**
 * Создает {@linkcode Result} типа `Err` (ошибка). Имплементирует
 * интерфейс {@linkcode IErrImpl}
 *
 * @export
 * @template E
 * @param {E} err
 * @return {IErrImpl<E>}
 */
function Err(err) {
    return new ErrImpl(err);
}
const ERR_EMPTY = Err(undefined);
/**
 * Возвращает ссылку на результат с пустой ошибкой, удобно использовать,
 * если `Result<T, undefined>`;
 */
Err.Empty = ERR_EMPTY;
Err.is = isErr;
Err.isStruct = isErrStruct;
function isOk(result) {
    if (isResult(result) && result['@type'] === OkType) {
        return true;
    }
    return false;
}
function isOkStruct(value) {
    return isResultStruct(value) && Object.prototype.hasOwnProperty.call(value, 'Ok');
}
function isErr(result) {
    if (isResult(result) && result['@type'] === ErrType) {
        return true;
    }
    return false;
}
function isErrStruct(value) {
    return isResultStruct(value) && Object.prototype.hasOwnProperty.call(value, 'Err');
}
function isResult(value) {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const { '@type': resultType } = value;
    return resultType === OkType || resultType === ErrType;
}
function isResultStruct(value) {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    return (Object.prototype.hasOwnProperty.call(value, 'Ok') ||
        Object.prototype.hasOwnProperty.call(value, 'Err'));
}
const Result = {
    collect(iter) {
        const result = [];
        for (const item of iter) {
            if (item.isErr()) {
                return item;
            }
            result.push(item.Ok);
        }
        return Ok(result);
    },
    is(value) {
        return isResult(value);
    },
    isStruct(value) {
        return isResultStruct(value);
    },
    isOk(result) {
        return isOk(result);
    },
    isOkStruct(value) {
        return isOkStruct(value);
    },
    isErr(result) {
        return isErr(result);
    },
    isErrStruct(value) {
        return isErrStruct(value);
    },
    fromStruct(value) {
        if (isOkStruct(value)) {
            if (Clone.isStruct(value.Ok)) {
                const result = restore(value.Ok);
                // TODO: Не обрабатывается случай с ошибкой десериализации значения Ok
                if (!result.isErr()) {
                    return Ok(result.Ok);
                }
            }
            return Ok(value.Ok);
        }
        if (Clone.isStruct(value.Err)) {
            const result = restore(value.Err);
            if (!result.isErr()) {
                return Ok(result.Ok);
            }
        }
        return Err(value.Err);
    },
    tryFromStruct(value) {
        if (!isResultStruct(value)) {
            return;
        }
        return Result.fromStruct(value);
    },
    restore() {
        return {
            [kStructuredCloneMarker]: NAMESPACE,
            from: Result.fromStruct,
        };
    },
};

export { Err, Ok, Result };
//# sourceMappingURL=result.js.map
